import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';

/**
 * Platform Notifications API client.
 *
 * Distinct from the existing deal-alert stream (`alertsApi.ts`) — these
 * notifications cover platform-level events (access requests, tenant
 * creation, invite expirations, billing, etc.) and surface in the
 * NotificationBell dropdown under a separate "Notificações" tab.
 *
 * Backend contract (proxied through BFF, all at /api/v1):
 *   GET    /notifications?unread=true&limit=20
 *   GET    /notifications/count           → { unread_count }
 *   PATCH  /notifications/{id}/read
 *   POST   /notifications/mark-all-read   → { marked_count }
 *   DELETE /notifications/{id}
 */

export type PlatformNotificationKind =
  | 'access_request_pending'
  | 'access_request_approved'
  | 'access_request_rejected'
  | 'tenant_created'
  | 'invite_expired'
  // Pre-D3 billing kinds
  | 'subscription_suspended'
  | 'subscription_cancelled'
  | 'payment_failed'
  // D3 W21 / arch#21 — billing-emitted lifecycle kinds.
  // See janus-billing/clients/notification_client.py::D3_W21_BILLING_KINDS.
  | 'subscription_renewed'
  | 'seat_added'
  | 'seat_removed'
  | 'plan_changed'
  | 'trial_ending'
  | 'credit_low'
  // RP-01 — sales-api emits these via the internal notifications endpoint
  // once the enrichment subscriber confirms reveal delivery (or the
  // reveal_timeout_worker sweeps a stale pending link).
  | 'contact_revealed'
  | 'contact_reveal_failed'
  // Envio LinkedIn fire-and-forget (crm-integration, ADR 2026-07-23) — só
  // desfechos ACIONÁVEIS chegam ao sino; sucesso vira passo concluído.
  | 'linkedin_send_failed'
  | 'linkedin_send_blocked'
  | 'linkedin_session_challenge'
  // SDR — tenant-management emits this when the voice agent books a meeting
  // that needs a tenant admin to assign it to an owner (action_url:
  // "/sdr/reunioes" → the SDR cockpit Reuniões inbox).
  | 'meeting_pending_assignment'
  // Nurture-régua — sales-api emits these when a contact opens/clicks an
  // email sent by a nurture sequence (action_url: "/pipeline/{deal_id}").
  | 'nurture_email_opened'
  | 'nurture_email_clicked'
  | 'generic';

export interface PlatformNotification {
  id: string;
  kind: PlatformNotificationKind;
  title: string;
  body?: string | null;
  action_url?: string | null;
  read: boolean;
  created_at: string;
}

interface NotificationsListResponse {
  /** Canonical envelope key (tenant-mgmt routers/notifications.py). */
  notifications?: PlatformNotification[];
  /** Legacy/back-compat envelope used by older tenant-mgmt builds.
   *  Tenant-mgmt currently emits BOTH for the soak; we read either so
   *  a future single-key deploy can't strand the dropdown empty. */
  items?: PlatformNotification[];
}

interface UnreadCountResponse {
  /** Canonical snake_case key (matches tenant-mgmt + BFF). */
  unread_count?: number;
  /** Defensive fallback: some legacy proxies camelCase the body. */
  unreadCount?: number;
  /** Older "count" field surfaced by a few historical builds — accept it
   *  as a last resort so a contract drift can't zero the badge. */
  count?: number;
}

interface MarkAllReadResponse {
  marked_count: number;
}

export const notificationsApi = {
  list: async (unreadOnly = false, limit = 20): Promise<PlatformNotification[]> => {
    const { data } = await api.get<NotificationsListResponse | PlatformNotification[]>(
      '/notifications',
      { params: { unread: unreadOnly, limit } },
    );
    if (Array.isArray(data)) return data;
    // Bug fix (D3): older tenant-mgmt builds emit `items` (legacy) without
    // the canonical `notifications` key. Reading only `notifications`
    // silently dropped every notification on those deploys, leaving the
    // dropdown empty even while `/count` correctly reported N>0. Accept
    // either envelope so we can never desync the bell badge from the list.
    if (data && typeof data === 'object') {
      if (Array.isArray(data.notifications)) return data.notifications;
      if (Array.isArray(data.items)) return data.items;
    }
    return [];
  },

  unreadCount: async (): Promise<number> => {
    const { data } = await api.get<UnreadCountResponse>('/notifications/count');
    if (!data || typeof data !== 'object') return 0;
    // Defensive: snake_case is canonical, but a misbehaving proxy/legacy
    // build may emit camelCase or `count`. Reading only `unread_count`
    // would zero the badge silently — surface whichever key arrived.
    return data.unread_count ?? data.unreadCount ?? data.count ?? 0;
  },

  markAsRead: async (id: string): Promise<void> => {
    await api.patch(`/notifications/${id}/read`);
  },

  markAllAsRead: async (): Promise<number> => {
    const { data } = await api.post<MarkAllReadResponse>('/notifications/mark-all-read');
    return data.marked_count ?? 0;
  },

  dismiss: async (id: string): Promise<void> => {
    await api.delete(`/notifications/${id}`);
  },
};

/* --------------------------------- Hooks --------------------------------- */

const NOTIFICATIONS_KEY = ['notifications'] as const;

export function useNotifications(enabled: boolean, unreadOnly = false, limit = 20) {
  return useQuery<PlatformNotification[]>({
    queryKey: [...NOTIFICATIONS_KEY, 'list', unreadOnly, limit],
    queryFn: () => notificationsApi.list(unreadOnly, limit),
    enabled,
    staleTime: 10_000,
  });
}

/**
 * Polled every 60s for the bell badge. Stays cheap thanks to the
 * dedicated `/count` endpoint (no payload) and a fail-soft default.
 */
export function useUnreadCount() {
  return useQuery<number>({
    queryKey: [...NOTIFICATIONS_KEY, 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 0,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  return useMutation<number, Error, void>({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => notificationsApi.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}
